import React, { useState, useEffect } from "react";
import CommunicationCenterEntity from "../../../entities/SuperAdminPage/CommunicationCenter";
import { communicationService } from "../../../api";
import AuthService from "../../../api/AuthService";
import LoaderHelper from "../../../utils/Loader/LoaderHelper";
import { useAlert } from "../../../context/AlertContext";

const CommunicationCenter = () => {
  const [groups, setGroups] = useState([]);
  const [countries, setCountries] = useState([]);
  const [historyData, setHistoryData] = useState([]);
  const [historyFilters, setHistoryFilters] = useState({
    channel: "all",
    status: "all",
  });
  
  const { showAlert } = useAlert();

  useEffect(() => {
    fetchGroups();
    fetchCountries();
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [historyFilters]);

  const fetchCountries = async () => {
    try {
      const result = await AuthService.getSelectedCountries();
      if (result && result.success && result.data) {
        let data = result.data.countrySelections?.[0]?.countries || result.data.countryList || result.data;
        setCountries(Array.isArray(data) ? data : []);
      } else {
        setCountries([]);
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
      setCountries([]);
    }
  };


  const fetchGroups = async () => {
    try {
      const result = await communicationService.getGroups();
      if (result && result.success) {
        setGroups(result.data || []);
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
    }
  };

  const fetchHistory = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await communicationService.getHistory(historyFilters);
      if (result && result.success) {
        setHistoryData(result.data || []);
      }
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleSend = async (payload) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await communicationService.send(payload);
      if (result && result.success) {
        showAlert(result.message || "Communication sent/scheduled successfully!", "success");
        fetchHistory(); // Refresh history
        return true;
      } else {
        showAlert(result?.message || "Failed to send communication", "error");
        return false;
      }
    } catch (error) {
      showAlert(error?.message || "An error occurred while sending", "error");
      return false;
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleFilterChange = (type, value) => {
    setHistoryFilters((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  const handleSearchUsers = async (search) => {
    try {
      const result = await communicationService.searchUsers(search);
      if (result && result.success) {
        return result.data?.data || [];
      }
      return [];
    } catch (error) {
      console.error("Error searching users:", error);
      return [];
    }
  };

  const handleCreateGroup = async (payload) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await communicationService.createGroup(payload);
      if (result && result.success) {
        showAlert(result.message || "Group created successfully!", "success");
        fetchGroups(); // Refresh groups
        return true;
      } else {
        showAlert(result?.message || "Failed to create group", "error");
        return false;
      }
    } catch (error) {
      showAlert(error?.message || "An error occurred while creating group", "error");
      return false;
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleUpdateGroup = async (id, payload) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await communicationService.updateGroup(id, payload);
      if (result && result.success) {
        showAlert(result.message || "Group updated successfully!", "success");
        fetchGroups(); // Refresh groups
        return true;
      } else {
        showAlert(result?.message || "Failed to update group", "error");
        return false;
      }
    } catch (error) {
      showAlert(error?.message || "An error occurred while updating group", "error");
      return false;
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  return (
    <CommunicationCenterEntity
      groups={groups}
      countries={countries}
      historyData={historyData}
      onSend={handleSend}
      historyFilters={historyFilters}
      onFilterChange={handleFilterChange}
      onSearchUsers={handleSearchUsers}
      onCreateGroup={handleCreateGroup}
      onUpdateGroup={handleUpdateGroup}
    />
  );
};

export default CommunicationCenter;
